'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// DOC: https://www.chartjs.org/docs
import {
    Chart as ChartJS,
    ChartType as ChartJSType,
    ChartOptions as ChartJSOptions,
    ChartDataset,
    ChartEvent,
    Color,
    ActiveElement,
    ActiveDataPoint,
    TooltipItem,
    PointStyle,
    TooltipLabelStyle
} from 'chart.js';
import 'chartjs-adapter-moment';

import {Button} from 'primereact/button';
import {Chart as PrimeChart} from 'primereact/chart';
import {InputNumber} from 'primereact/inputnumber';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import {
    LoadConfigParameters,
    LoadConfigResult,
    LoadDataParameters
} from '~/shared/src/utilities/modules/chart';
import {Query} from '~/shared/src/utilities/query';
import {
    DataDescriptor,
    DataStructure
} from '~/shared/src/utilities/dataStructure';
import {
    ChartType,
    LegendAlign,
    LegendPosition,
    TimeSeriesIntervalUnit
} from '~/shared/src/utilities/chart';
import {ChartDataSet} from '~/shared/src/utilities/chartDataSet';
import {ChartScale} from '~/shared/src/utilities/chartScale';
import {valuePrecision} from '~/shared/src/utilities/modules/finder';
import {
    LoadMetaDataRowData,
    LoadMetaDataRowDataMapping
} from '~/shared/src/utilities/modules/overview';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {FilterMatchMode} from '~/shared/src/validationSchemes/modules/overview';

import TooltipShadow from '~/frontend/src/components/base/Chart/plugins/tooltipShadow';
//import DatasetShadow from '~/frontend/src/components/base/Chart/plugins/datasetShadow';
import {FilterField} from '~/frontend/src/components/base/shared/field/FilterField';
import ValueSetField from '~/frontend/src/components/base/shared/field/ValueSetField';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {
    renderValue,
    resolveStyle
} from '~/frontend/src/utilities/valueRenderers';
import {getValueForDataDescriptorFromRowData} from '~/frontend/src/utilities/dataUtils';
import {getValueDisplayName} from '~/frontend/src/utilities/renderHelpers';
import {getColorFromColorPalette} from '~/frontend/src/components/base/shared/field/valueTypeFields/ColorField';

import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import loadData from '~/frontend/src/actions/data/loadData';
import {
    setElusiveData as origSetElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';


export type TimeSeriesParameters = Pick<LoadDataParameters, 'timeSeriesIntervalCount' | 'timeSeriesIntervalEnd' | 'timeSeriesIntervalStart' | 'timeSeriesIntervalUnit'>;

export type DefaultProps = {
    schema? : Schema;
    /**
     * Is this overview standalone.
     */
    mode? : 'standalone' | 'embedded';

    limitToHeight? : boolean;
    type? : ChartType;
    legendAlign? : LegendAlign;
    legendPosition? : LegendPosition;
    timeSeriesIntervalUnit? : false | TimeSeriesIntervalUnit;
    timeSeriesShowIntervalSelector? : boolean;
    timeSeriesShowTimeRangeSelector? : boolean;

    queryType? : Query[ 'type' ];
};
export const defaultProps : Required<DefaultProps> = {
    schema                         : 'DATA',
    mode                           : 'standalone',
    limitToHeight                  : false,
    type                           : 'line',
    legendAlign                    : 'start',
    legendPosition                 : 'bottom',
    timeSeriesIntervalUnit         : false,
    timeSeriesShowIntervalSelector : true,
    timeSeriesShowTimeRangeSelector: true,
    queryType                      : 'DataStructure'
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    id? : string;

    /**
     * The dataContext for the form data to access global.data with.
     */
    dataContext : string;

    /**
     * The key of the overview config.
     */
    viewKey : string;

    /**
     * A hash of the loaded overview configuration to detect changes.
     */
    configHash? : string;

    dataStructure? : DataStructure;

    xAxisLabelDataDescriptor? : DataDescriptor;

    dataDescriptors? : DataDescriptor[];

    dataSets? : ChartDataSet[];

    scales? : ChartScale[];

    timeSeriesIntervalCount? : number;
    timeSeriesIntervalEnd? : string;
    timeSeriesIntervalStart? : string;

    /**
     * Function invoked if config was successfully loaded.
     *
     * @param {LoadConfigResult[ 'data' ]} config
     */
    onConfigLoad? : ( config : LoadConfigResult[ 'data' ] ) => void;

    /**
     * Function invoked if data was successfully loaded.
     */
    onDataLoad? : () => void;

    onBeforeReloadData? : ( timeSeriesParameters : TimeSeriesParameters ) => boolean;

    /**
     * Additional parameters which have to be relayed to the CFG:Query used to load the results.
     */
    additionalQueryParameters? : Required<LoadDataParameters[ 'parameters' ]>;

};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

type ChartDataSetData = ChartDataset<'bar'>
    | ChartDataset<'line'>
    | ChartDataset<'scatter'>
    | ChartDataset<'bubble'>
    | ChartDataset<'pie'>
    | ChartDataset<'doughnut'>
    | ChartDataset<'polarArea'>
    | ChartDataset<'radar'>;

type ChartOptions = ChartJSOptions<'bar'>
    | ChartJSOptions<'line'>
    | ChartJSOptions<'scatter'>
    | ChartJSOptions<'bubble'>
    | ChartJSOptions<'pie'>
    | ChartJSOptions<'doughnut'>
    | ChartJSOptions<'polarArea'>
    | ChartJSOptions<'radar'>;

type LegendItem = {
    label : string;
    dataSetIndex : number;
    dataIndex? : number;
    backgroundColor : string;
    borderColor : string;
    hidden : boolean;
};

export type ElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    configLoaded? : boolean;
    dataLoaded? : boolean;
    xAxisValues? : string[];
    legendItems? : LegendItem[];
    options? : ChartOptions;
    dataSetData? : ChartDataSetData[];
    metaData? : PanelElusiveDataState[ 'metaData' ] & {
        rowData? : LoadMetaDataRowData[];
        rowDataMapping? : LoadMetaDataRowDataMapping;
    }
    timeSeriesIntervalCount? : number;
    timeSeriesIntervalEnd? : string;
    timeSeriesIntervalStart? : string;
    timeSeriesIntervalUnit? : TimeSeriesIntervalUnit;
};

const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;
const setElusiveData = origSetElusiveData as ( panelKey : string, data : ElusiveData ) => SetElusiveDataAction;


const getColorScheme = () => matchMedia( '(prefers-color-scheme: dark)' ).matches ? 'dark' : 'light';

const COLOR_SCHEME_COLORS = {
    light: {
        tooltipBackground: '#ffffff',
        tooltipText      : '#495057',
        scaleBorder      : '#6c757d',
        scaleGrid        : '#ced4da',
        scaleTickBackdrop: '#ffffff',
        scaleTickText    : '#495057'
    },
    dark : {
        tooltipBackground: '#383838',
        tooltipText      : '#ffffffde',
        scaleBorder      : '#868686',
        scaleGrid        : '#383838',
        scaleTickBackdrop: '#1e1e1e',
        scaleTickText    : '#ffffffde'
    }
};


const Chart = ( props : AllProps ) => {
    const dispatch : AppDispatch                        = useDispatch(),
          {
              additionalQueryParameters,
              configHash,
              dataContext,
              dataDescriptors,
              dataSets,
              dataStructure,
              id,
              legendAlign,
              legendPosition,
              limitToHeight,
              mode,
              onBeforeReloadData,
              onConfigLoad,
              onDataLoad,
              queryType,
              scales: rawScales,
              schema,
              timeSeriesShowIntervalSelector,
              timeSeriesShowTimeRangeSelector,
              type,
              uid,
              viewKey,
              xAxisLabelDataDescriptor
          }                                             = props,
          elementId                                     = id ?? `${uid}_chartView`,
          user                                          = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                       = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          elusiveData                                   = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          dataElusiveData                               = useSelector( ( state : StoreState ) => state.elusiveData?.[ dataContext ] as ElusiveData | undefined ),
          isLoading                                     = useRef<boolean>( false ),
          hasRendered                                   = useRef<boolean>( false ),
          chart                                         = useRef<PrimeChart>( null ),
          lastDataSetIndex                              = useRef<number | undefined>( undefined ),
          lastDataIndex                                 = useRef<number | undefined>( undefined ),
          ignoreHover                                   = useRef<boolean>( false ),
          lastLegendItemsVisibility                     = useRef<boolean[]>( [] ),
          DSD                                           = useRef<ChartDataSetData[] | undefined>( undefined ),
          [ colorScheme, setColorScheme ]               = useState<'dark' | 'light'>( getColorScheme() ),
          [ rerenderToggle, setRerenderToggle ]         = useState<number>( 0 ),
          [ chartWrapperHeight, setChartWrapperHeight ] = useState<number>( 0 ),
          [ chartWrapperWidth, setChartWrapperWidth ]   = useState<number>( 0 ),
          configRequestUid                              = 'loadConfig',
          dataRequestUid                                = 'loadData',
          isConfigLoaded                                = useMemo(
              () => {
                  return elusiveData?.configLoaded ?? false;
              },
              [
                  elusiveData?.configLoaded ?? false
              ]
          ),
          isDataLoaded                                  = useMemo(
              () => {
                  return elusiveData?.dataLoaded ?? false;
              },
              [
                  elusiveData?.dataLoaded ?? false
              ]
          ),
          language                                      = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions                            = useMemo(
              () => {
                  return JSON.stringify( dataElusiveData?.data ?? {} );
              },
              [
                  dataElusiveData?.data
              ]
          ),
          dataSchema                                    = useMemo(
              () => {
                  return ( dataStructure?.item?.objectSchema?.toUpperCase() ?? schema ?? 'DATA' ) as Schema;
              },
              [
                  dataStructure,
                  schema
              ]
          ),
          timeSeriesIntervalUnitValueSetId              = useMemo(
              () => {
                  return metaModel[ 'CFG' ].getValueSet( 'TimeSeriesIntervalUnit' )?.id ?? 0;
              },
              [
                  metaModel[ 'CFG' ]
              ]
          ),
          isTimeSeries                                  = useMemo(
              () => {
                  return !!props.timeSeriesIntervalUnit;
              },
              [
                  props.timeSeriesIntervalUnit
              ]
          ),
          timeSeriesIntervalCount                       = useMemo(
              () => {
                  return elusiveData?.timeSeriesIntervalCount ?? props.timeSeriesIntervalCount;
              },
              [
                  elusiveData?.timeSeriesIntervalCount,
                  props.timeSeriesIntervalCount
              ]
          ),
          timeSeriesIntervalEnd                         = useMemo(
              () => {
                  return elusiveData?.timeSeriesIntervalEnd ?? props.timeSeriesIntervalEnd;
              },
              [
                  elusiveData?.timeSeriesIntervalEnd,
                  props.timeSeriesIntervalEnd
              ]
          ),
          timeSeriesIntervalStart                       = useMemo(
              () => {
                  return elusiveData?.timeSeriesIntervalStart ?? props.timeSeriesIntervalStart;
              },
              [
                  elusiveData?.timeSeriesIntervalStart,
                  props.timeSeriesIntervalStart
              ]
          ),
          timeSeriesIntervalUnit                        = useMemo(
              () => {
                  return elusiveData?.timeSeriesIntervalUnit ?? props.timeSeriesIntervalUnit;
              },
              [
                  elusiveData?.timeSeriesIntervalUnit,
                  props.timeSeriesIntervalUnit
              ]
          ),
          buildLabel                                    = useCallback(
              ( rowData : LoadMetaDataRowData ) => {
                  let retVal = '';

                  if ( dataStructure && xAxisLabelDataDescriptor && rowData ) {
                      const value = getValueForDataDescriptorFromRowData(
                          dataContext,
                          xAxisLabelDataDescriptor,
                          rowData,
                          dataStructure
                      );

                      if ( value ) {
                          // enum
                          if ( Array.isArray( value.value ) ) {
                              retVal = value.value.join( ', ' );
                          }

                          // not empty
                          else if ( value.value !== undefined && value.value !== null && String( value.value ).trim() !== '' ) {
                              // ValueSet
                              if ( value.valueSetId ) {
                                  retVal = getValueDisplayName( value.valueSetId, value.value, schema );
                              }

                              // normal
                              else {
                                  const renderValueParams : any = {
                                      schema,
                                      language : user?.language,
                                      valueUnit: value.valueUnit,
                                      valuePrecision
                                  };

                                  retVal = renderValue( value.valueType, value.value, renderValueParams, false, true ) as string;
                              }
                          }
                          else {
                              retVal = 'n/a';
                          }
                      }
                      else {
                          retVal = 'n/a';
                      }
                  }

                  return retVal;
              },
              [
                  dataContext,
                  dataStructure,
                  xAxisLabelDataDescriptor
              ]
          ),
          xAxisValues                                   = useMemo(
              () => {
                  const retVal : string[] = [];

                  if ( elusiveData?.metaData?.rowData ) {
                      if ( type === 'bar' && dataSets?.length === 1 && dataSets[ 0 ].data.convertBarsToDataSets ) {
                          retVal.push( '' );
                      }
                      else if ( !isTimeSeries && type !== 'doughnut' && type !== 'pie' && type !== 'polarArea' ) {
                          for ( const rowDataRaw of elusiveData.metaData.rowData ) {
                              retVal.push( buildLabel( rowDataRaw as LoadMetaDataRowData ) );
                          }
                      }
                  }

                  return retVal;
              },
              [
                  buildLabel,
                  dataSets,
                  elusiveData?.metaData?.rowData,
                  elusiveData?.metaData?.rowDataMapping,
                  language,
                  messageCatalogTimestamp,
                  isTimeSeries,
                  type
              ]
          ),
          legendItems                                   = useMemo(
              () => {
                  const retVal : LegendItem[]                = [],
                        newLegendItemsVisibility : boolean[] = [];

                  if ( dataStructure && dataSets && elusiveData?.metaData?.rowData ) {
                      for ( let dataSetCount = 0; dataSetCount < dataSets.length; dataSetCount++ ) {
                          const dataSet        = dataSets[ dataSetCount ],
                                dataSetType    = type === 'bar' || type === 'line'
                                    ? ( dataSet.data.type ?? type ) as Exclude<ChartJSType, 'bubble' | 'scatter'>
                                    : type,
                                dataDescriptor = dataSet.dataDescriptor;

                          if ( dataSetType === 'bar' && dataSets?.length === 1 && dataSets[ 0 ].data.convertBarsToDataSets ) {
                              for ( let rowDataCount = 0; rowDataCount < elusiveData.metaData.rowData.length; rowDataCount++ ) {
                                  const rowData                                           = elusiveData.metaData.rowData[ rowDataCount ] as LoadMetaDataRowData,
                                        data : { [ dataDescriptorAlias : string ] : any } = {},
                                        label                                             = buildLabel( rowData );

                                  let backgroundColor : string = getColorFromColorPalette( rowDataCount ),
                                      borderColor : string     = 'transparent';

                                  if ( dataDescriptors ) {
                                      for ( const dataDescriptor1 of dataDescriptors ) {
                                          if ( queryType === 'DataStructure' ) {
                                              const value = getValueForDataDescriptorFromRowData(
                                                  dataContext,
                                                  dataDescriptor1,
                                                  rowData,
                                                  dataStructure
                                              );

                                              if ( value ) {
                                                  data[ dataDescriptor1.alias ] = value.value;
                                              }
                                          }

                                          if ( !data[ dataDescriptor1.alias ] ) {
                                              data[ dataDescriptor1.alias ] = rowData[ elusiveData?.metaData?.rowDataMapping?.[ dataDescriptor1.alias ] ?? '' ]
                                                  ?? rowData[ dataDescriptor1.alias ];
                                          }
                                      }
                                  }

                                  // process style
                                  if ( dataSet.styles?.style?.length ) {
                                      const style = resolveStyle( dataSet.styles.style, data );

                                      if ( style[ 'background-color' ] ) {
                                          backgroundColor = style[ 'background-color' ];
                                      }

                                      if ( style[ 'color' ] ) {
                                          borderColor = style[ 'border-color' ];
                                      }
                                  }

                                  retVal.push( {
                                      label,
                                      dataSetIndex: rowDataCount,
                                      backgroundColor,
                                      borderColor,
                                      hidden      : false
                                  } );
                                  newLegendItemsVisibility.push( false );
                              }
                          }
                          else if ( dataSetType === 'doughnut' || dataSetType === 'pie' || dataSetType === 'polarArea' ) {
                              for ( let rowCount = 0; rowCount < elusiveData.metaData.rowData.length; rowCount++ ) {
                                  const rowData                                           = elusiveData.metaData.rowData[ rowCount ] as LoadMetaDataRowData,
                                        data : { [ dataDescriptorAlias : string ] : any } = {};

                                  let backgroundColor : string = getColorFromColorPalette( dataSetCount + rowCount ),
                                      borderColor : string     = 'transparent';

                                  if ( dataDescriptors ) {
                                      for ( const dataDescriptor1 of dataDescriptors ) {
                                          if ( queryType === 'DataStructure' ) {
                                              const value = getValueForDataDescriptorFromRowData(
                                                  dataContext,
                                                  dataDescriptor1,
                                                  rowData,
                                                  dataStructure
                                              );

                                              if ( value ) {
                                                  data[ dataDescriptor1.alias ] = value.value;
                                              }
                                          }

                                          if ( !data[ dataDescriptor1.alias ] ) {
                                              data[ dataDescriptor1.alias ] = rowData[ elusiveData?.metaData?.rowDataMapping?.[ dataDescriptor1.alias ] ?? '' ]
                                                  ?? rowData[ dataDescriptor1.alias ];
                                          }
                                      }
                                  }

                                  // process style
                                  if ( dataSet.styles?.style?.length ) {
                                      const style = resolveStyle( dataSet.styles.style, data );

                                      if ( style[ 'background-color' ] ) {
                                          backgroundColor = style[ 'background-color' ];
                                      }

                                      if ( style[ 'color' ] ) {
                                          borderColor = style[ 'border-color' ];
                                      }
                                  }

                                  retVal.push( {
                                      label       : buildLabel( rowData ),
                                      dataSetIndex: dataSetCount,
                                      dataIndex   : rowCount,
                                      backgroundColor,
                                      borderColor,
                                      hidden      : false
                                  } );
                                  newLegendItemsVisibility.push( false );
                              }

                              break;
                          }
                          else {
                              let label : string | undefined,
                                  backgroundColor = dataSet.data.backgroundColor,
                                  borderColor     = dataSet.data.borderColor;

                              if ( dataSetType === 'bar' ) {
                                  if ( !backgroundColor ) {
                                      backgroundColor = getColorFromColorPalette( dataSetCount );
                                  }
                                  if ( !borderColor ) {
                                      borderColor = 'transparent';
                                  }
                              }
                              else {
                                  if ( !backgroundColor || !dataSet.data.lineFill ) {
                                      backgroundColor = getColorFromColorPalette( dataSetCount );
                                  }
                                  if ( !borderColor ) {
                                      borderColor = getColorFromColorPalette( dataSetCount );
                                  }
                              }

                              if ( dataSet.messages?.label ) {
                                  label = __( typeof dataSet.messages.label === 'string' ? dataSet.messages.label : dataSet.messages.label.key );
                              }
                              else if ( dataDescriptor ) {
                                  if ( queryType === 'DataStructure' ) {
                                      label = __( dataUtils.getMetaModelLabelMsgId( dataDescriptor, dataStructure, schema ) );
                                  }
                                  else {
                                      label = dataDescriptor.field;
                                  }
                              }

                              if ( !label ) {
                                  label = dataSet.data.name;
                              }

                              retVal.push( {
                                  label,
                                  dataSetIndex: dataSetCount,
                                  dataIndex   : undefined,
                                  backgroundColor,
                                  borderColor,
                                  hidden      : false
                              } );
                              newLegendItemsVisibility.push( false );
                          }
                      }
                  }

                  lastLegendItemsVisibility.current = newLegendItemsVisibility;

                  return retVal;
              },
              [
                  buildLabel,
                  dataContext,
                  dataDescriptors,
                  dataSets,
                  dataStructure,
                  elusiveData?.metaData?.rowData,
                  elusiveData?.metaData?.rowDataMapping,
                  globalDataVersions,
                  language,
                  messageCatalogTimestamp,
                  type
              ]
          ),
          legendClassNames                              = useMemo(
              () => {
                  return `yn-chart-legend-align-${legendAlign} yn-chart-legend-position-${legendPosition}`;
              },
              [
                  legendAlign,
                  legendPosition
              ]
          ),
          scaleMapping                                  = useMemo(
              () => {
                  const retVal : { [ scaleId : string ] : 'x' | 'y' | 'r' } = {},
                        usedAxes : { [ axis : string ] : boolean }          = {};

                  if ( rawScales?.length ) {
                      for ( const scale of rawScales ) {
                          const scaleId = scale.data.key;

                          if ( scaleId !== scale.data.axis && !usedAxes[ scale.data.axis ] ) {
                              retVal[ scaleId ] = scale.data.axis;
                          }

                          usedAxes[ scale.data.axis ] = true;
                      }
                  }

                  return retVal;
              },
              [
                  rawScales
              ]
          ),
          dataSetData                                   = useMemo(
              () => {
                  const retVal : ChartDataSetData[] = [];

                  if ( dataStructure && dataSets && elusiveData?.metaData?.rowData ) {
                      for ( let dataSetCount = 0; dataSetCount < dataSets.length; dataSetCount++ ) {
                          const dataSet     = dataSets[ dataSetCount ],
                                dataSetType = type === 'bar' || type === 'line'
                                    ? ( dataSet.data.type ?? type ) as Exclude<ChartJSType, 'bubble' | 'scatter'>
                                    : type;

                          if ( dataSetType === 'bar' && dataSets?.length === 1 && dataSets[ 0 ].data.convertBarsToDataSets ) {
                              const dataDescriptor = dataSets[ 0 ].dataDescriptor;

                              for ( let rowDataCount = 0; rowDataCount < elusiveData.metaData.rowData.length; rowDataCount++ ) {
                                  const rowData                                           = elusiveData.metaData.rowData[ rowDataCount ] as LoadMetaDataRowData,
                                        data : { [ dataDescriptorAlias : string ] : any } = {},
                                        dataValue                                         = rowData[ elusiveData?.metaData?.rowDataMapping?.[ dataDescriptor?.alias ?? '' ] ?? '' ] ?? rowData[ dataDescriptor?.alias ?? '' ] ?? null;

                                  let backgroundColor : string = getColorFromColorPalette( rowDataCount ),
                                      borderColor : string     = 'transparent';

                                  if ( dataDescriptors ) {
                                      for ( const dataDescriptor1 of dataDescriptors ) {
                                          if ( queryType === 'DataStructure' ) {
                                              const value = getValueForDataDescriptorFromRowData(
                                                  dataContext,
                                                  dataDescriptor1,
                                                  rowData,
                                                  dataStructure
                                              );

                                              if ( value ) {
                                                  data[ dataDescriptor1.alias ] = value.value;
                                              }
                                          }

                                          if ( !data[ dataDescriptor1.alias ] ) {
                                              data[ dataDescriptor1.alias ] = rowData[ elusiveData?.metaData?.rowDataMapping?.[ dataDescriptor1.alias ] ?? '' ]
                                                  ?? rowData[ dataDescriptor1.alias ];
                                          }
                                      }
                                  }

                                  // process style
                                  if ( dataSet.styles?.style?.length ) {
                                      const style = resolveStyle( dataSet.styles.style, data );

                                      if ( style[ 'background-color' ] ) {
                                          backgroundColor = style[ 'background-color' ];
                                      }

                                      if ( style[ 'color' ] ) {
                                          borderColor = style[ 'border-color' ];
                                      }
                                  }

                                  retVal.push( {
                                      data                : [
                                          {
                                              x: dataSet.data.horizontal
                                                  ? isNaN( Number( dataValue ) )
                                                      ? null
                                                      : Number( dataValue )
                                                  : 0,
                                              y: dataSet.data.horizontal
                                                  ? 0
                                                  : isNaN( Number( dataValue ) )
                                                      ? null
                                                      : Number( dataValue )
                                          }
                                      ] as any[],
                                      backgroundColor     : backgroundColor,
                                      hoverBackgroundColor: backgroundColor,
                                      borderColor         : borderColor,
                                      hoverBorderColor    : borderColor
                                  } );
                              }
                          }
                          else {
                              const dataSetDataEntry : ChartDataSetData = {
                                        data: [] as any[]
                                    },
                                    dataDescriptor                      = dataSet.dataDescriptor;

                              if ( dataSetType === 'doughnut' || dataSetType === 'pie' || dataSetType === 'polarArea' ) {
                                  dataSetDataEntry.backgroundColor = [];
                                  dataSetDataEntry.hoverBackgroundColor = [];
                                  dataSetDataEntry.borderColor = [];
                                  dataSetDataEntry.hoverBorderColor = [];
                              }

                              // extract data for dataSet
                              // and set colors for dataSet types doughnut, bar and polarArea
                              for ( let rowCount = 0; rowCount < elusiveData.metaData.rowData.length; rowCount++ ) {
                                  const rowData                                           = elusiveData.metaData.rowData[ rowCount ] as LoadMetaDataRowData,
                                        data : { [ dataDescriptorAlias : string ] : any } = {},
                                        dataValue                                         = rowData[ elusiveData?.metaData?.rowDataMapping?.[ dataDescriptor?.alias ?? '' ] ?? '' ] ?? rowData[ dataDescriptor?.alias ?? '' ] ?? null;

                                  if ( type === 'bar' || type === 'line' ) {
                                      dataSetDataEntry.data.push( {
                                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                          // @ts-expect-error
                                          x: dataSet.data.horizontal
                                              ? isNaN( Number( dataValue ) )
                                                  ? null
                                                  : Number( dataValue )
                                              : isTimeSeries ? rowData.x as number : buildLabel( rowData ),
                                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                          // @ts-expect-error
                                          y: dataSet.data.horizontal
                                              ? isTimeSeries ? rowData.x as number : buildLabel( rowData )
                                              : isNaN( Number( dataValue ) )
                                                  ? null
                                                  : Number( dataValue )
                                      } );
                                  }
                                  else if ( type === 'polarArea' || type === 'radar' ) {
                                      dataSetDataEntry.data.push( {
                                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                          // @ts-expect-error
                                          r: isNaN( Number( dataValue ) )
                                              ? null
                                              : Number( dataValue )
                                      } );
                                  }
                                  else {
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-expect-error
                                      dataSetDataEntry.data.push( isNaN( Number( dataValue ) ) ? null : Number( dataValue ) );
                                  }

                                  if ( dataSetType === 'doughnut' || dataSetType === 'pie' || dataSetType === 'polarArea' ) {
                                      let backgroundColor : string = getColorFromColorPalette( dataSetCount + rowCount ),
                                          borderColor : string     = 'transparent';

                                      if ( dataDescriptors ) {
                                          for ( const dataDescriptor1 of dataDescriptors ) {
                                              if ( queryType === 'DataStructure' ) {
                                                  const value = getValueForDataDescriptorFromRowData(
                                                      dataContext,
                                                      dataDescriptor1,
                                                      rowData,
                                                      dataStructure
                                                  );

                                                  if ( value ) {
                                                      data[ dataDescriptor1.alias ] = value.value;
                                                  }
                                              }

                                              if ( !data[ dataDescriptor1.alias ] ) {
                                                  data[ dataDescriptor1.alias ] = rowData[ elusiveData?.metaData?.rowDataMapping?.[ dataDescriptor1.alias ] ?? '' ]
                                                      ?? rowData[ dataDescriptor1.alias ];
                                              }
                                          }
                                      }

                                      // process style
                                      if ( dataSet.styles?.style?.length ) {
                                          const style = resolveStyle( dataSet.styles.style, data );

                                          if ( style[ 'background-color' ] ) {
                                              backgroundColor = style[ 'background-color' ];
                                          }

                                          if ( style[ 'color' ] ) {
                                              borderColor = style[ 'border-color' ];
                                          }
                                      }

                                      ( dataSetDataEntry.backgroundColor as string[] ).push( backgroundColor );
                                      ( dataSetDataEntry.hoverBackgroundColor as string[] ).push( backgroundColor );
                                      ( dataSetDataEntry.borderColor as string[] ).push( borderColor );
                                      ( dataSetDataEntry.hoverBorderColor as string[] ).push( borderColor );
                                  }
                              }

                              // set colors for all other dataSet types
                              if ( dataSetType === 'line' || dataSetType === 'radar' ) {
                                  dataSetDataEntry.backgroundColor = dataSet.data.backgroundColor ?? 'transparent';
                                  dataSetDataEntry.hoverBackgroundColor = dataSet.data.backgroundColor ?? 'transparent';
                                  dataSetDataEntry.borderColor = dataSet.data.borderColor ?? getColorFromColorPalette( dataSetCount );
                                  dataSetDataEntry.hoverBorderColor = dataSet.data.borderColor ?? getColorFromColorPalette( dataSetCount );
                              }
                              else if ( dataSetType === 'bar' ) {
                                  dataSetDataEntry.backgroundColor = dataSet.data.backgroundColor ?? getColorFromColorPalette( dataSetCount );
                                  dataSetDataEntry.hoverBackgroundColor = dataSet.data.backgroundColor ?? getColorFromColorPalette( dataSetCount );
                                  dataSetDataEntry.borderColor = dataSet.data.borderColor ?? 'transparent';
                                  dataSetDataEntry.hoverBorderColor = dataSet.data.borderColor ?? 'transparent';
                              }

                              // different type than chart but only for 'bar' and 'line'
                              if (
                                  ( type === 'bar' || type === 'line' )
                                  && ( dataSetType === 'bar' || dataSetType === 'line' )
                                  && type !== dataSetType
                              ) {
                                  dataSetDataEntry.type = dataSetType;
                              }

                              // type 'bar' specific
                              if ( dataSetType === 'bar' ) {
                                  if ( dataSet.data.borderColor ) {
                                      dataSetDataEntry.borderWidth = 2;
                                  }

                                  if ( dataSet.data.stackGroup ) {
                                      dataSetDataEntry.stack = dataSet.data.stackGroup;
                                  }
                              }

                              // type 'line' specific
                              else if ( dataSetType === 'line' ) {
                                  if ( dataSet.data.stackGroup ) {
                                      dataSetDataEntry.stack = dataSet.data.stackGroup;
                                  }

                                  if ( dataSet.data.lineFill ) {
                                      ( dataSetDataEntry as ChartDataset<'line'> ).fill = true;
                                  }

                                  if ( dataSet.data.lineStepMode ) {
                                      ( dataSetDataEntry as ChartDataset<'line'> ).stepped = dataSet.data.lineStepMode;
                                  }

                                  if ( dataSet.data.lineSmooth ) {
                                      ( dataSetDataEntry as ChartDataset<'line'> ).cubicInterpolationMode = 'default';
                                      ( dataSetDataEntry as ChartDataset<'line'> ).tension = 0.4;
                                  }
                              }

                              // type 'radar' specific
                              else if ( dataSetType === 'radar' ) {
                                  if ( dataSet.data.lineFill ) {
                                      ( dataSetDataEntry as ChartDataset<'radar'> ).fill = true;
                                  }
                                  else {
                                      ( dataSetDataEntry as ChartDataset<'radar'> ).fill = false;
                                  }
                              }

                              if ( dataSet.scale ) {
                                  if ( dataSetType === 'bar' || dataSetType === 'line' || dataSetType === 'radar' ) {
                                      ( dataSetDataEntry as ChartDataset<'bar'> | ChartDataset<'line'> | ChartDataset<'radar'> ).yAxisID = scaleMapping[ dataSet.scale ] ?? dataSet.scale;
                                  }
                              }

                              retVal.push( dataSetDataEntry );
                          }
                      }
                  }

                  return retVal;
              },
              [
                  buildLabel,
                  dataContext,
                  dataDescriptors,
                  dataSets,
                  dataStructure,
                  elusiveData?.metaData?.rowData,
                  elusiveData?.metaData?.rowDataMapping,
                  globalDataVersions,
                  isTimeSeries,
                  language,
                  messageCatalogTimestamp,
                  scaleMapping,
                  type
              ]
          ),
          scales                                        = useMemo(
              () => {
                  let retVal : ChartScale[] | undefined;

                  if ( rawScales?.length ) {
                      retVal = rawScales;
                  }

                  // build scale for radar chart since default behaviour is buggy
                  else if ( type === 'radar' && dataSets?.length && elusiveData?.metaData?.rowData?.length ) {
                      const aliases : string[] = [];

                      let min : number | undefined,
                          max : number | undefined;

                      for ( const dataSet of dataSets ) {
                          if ( dataSet.dataDescriptor && !aliases.includes( dataSet.dataDescriptor.alias ) ) {
                              aliases.push( dataSet.dataDescriptor.alias );
                          }
                      }

                      for ( const rowData of elusiveData.metaData.rowData ) {
                          for ( const alias of aliases ) {
                              const value = Number( rowData[ alias ] );

                              if ( min !== undefined ) {
                                  min = Math.min( min, value );
                              }
                              else {
                                  min = value;
                              }
                              if ( max !== undefined ) {
                                  max = Math.max( max, value );
                              }
                              else {
                                  max = value;
                              }
                          }
                      }

                      retVal = [
                          {
                              data: {
                                  id          : -1,
                                  key         : 'r',
                                  name        : 'r',
                                  axis        : 'r',
                                  position    : 'start',
                                  type        : 'linear',
                                  min         : min !== undefined ? Math.min( 0, min ) : min,
                                  suggestedMin: min !== undefined ? Math.min( 0, min ) : min,
                                  max         : max,
                                  suggestedMax: max
                              }
                          }
                      ];
                  }

                  return retVal;
              },
              [
                  dataSets,
                  elusiveData?.metaData?.rowData,
                  rawScales,
                  type
              ]
          ),
          loadConfig                                    = useCallback(
              async () => {
                  isLoading.current = true;

                  dispatch( startRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  try {
                      const params : LoadConfigParameters = {
                                viewKey
                            },
                            response                      = await xhr.post(
                                `${BASE_URL}/api/chart/loadConfig`,
                                params,
                                undefined,
                                uid,
                                configRequestUid
                            );

                      if ( response?.data ) {
                          const result = response.data as LoadConfigResult,
                                config = result.data;

                          dispatch( setParams( uid, {
                              configHash              : config.configHash,
                              type                    : config.type,
                              dataDescriptors         : config.dataDescriptors,
                              dataSets                : config.dataSets,
                              dataStructure           : config.dataStructure,
                              queryType               : config.queryType,
                              scales                  : config.scales,
                              schema                  : config.schema,
                              legendAlign             : config.legendAlign,
                              legendPosition          : config.legendPosition,
                              timeSeriesIntervalCount : config.timeSeriesIntervalCount,
                              timeSeriesIntervalEnd   : config.timeSeriesIntervalEnd,
                              timeSeriesIntervalStart : config.timeSeriesIntervalStart,
                              timeSeriesIntervalUnit  : config.timeSeriesIntervalUnit,
                              xAxisLabelDataDescriptor: config.xAxisLabelDataDescriptor
                          } ) );

                          onConfigLoad?.( config );
                      }

                      dispatch( setElusiveData( uid, { configLoaded: true } ) );
                  }
                  finally {
                      // always end the masking request, even if loading/processing threw
                      dispatch( endRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      isLoading.current = false;
                  }
              },
              [
                  uid,
                  viewKey
              ]
          ),
          load                                          = useCallback(
              async () => {
                  isLoading.current = true;

                  dispatch( startRequest(
                      () => {
                      },
                      uid,
                      'processing'
                  ) );

                  try {
                      const params : LoadDataParameters = {
                                viewKey,
                                configHash,
                                parameters             : additionalQueryParameters,
                                timeSeriesIntervalCount: timeSeriesIntervalUnit ? timeSeriesIntervalCount : undefined,
                                timeSeriesIntervalEnd  : timeSeriesIntervalUnit ? timeSeriesIntervalEnd : undefined,
                                timeSeriesIntervalStart: timeSeriesIntervalUnit ? timeSeriesIntervalStart : undefined,
                                timeSeriesIntervalUnit : timeSeriesIntervalUnit ? timeSeriesIntervalUnit : undefined
                            },
                            response                    = await dispatch(
                                loadData(
                                    'chart/loadData',
                                    dataContext,
                                    uid,
                                    dataRequestUid,
                                    dataSchema,
                                    {
                                        params,
                                        mergeMode        : mode === 'standalone' ? 'override' : 'keepLoaded',
                                        extraMetaDataKeys: [ 'rowData', 'rowDataMapping' ]
                                    }
                                )
                            );

                      if ( response ) {
                          onDataLoad?.();
                      }

                      dispatch( setElusiveData( uid, { dataLoaded: true } ) );
                  }
                  finally {
                      // always end the masking request, even if loading/processing threw
                      dispatch( endRequest(
                          () => {
                          },
                          uid,
                          'processing'
                      ) );

                      isLoading.current = false;
                  }
              },
              [
                  additionalQueryParameters,
                  configHash,
                  dataContext,
                  dataSchema,
                  timeSeriesIntervalCount,
                  timeSeriesIntervalEnd,
                  timeSeriesIntervalStart,
                  timeSeriesIntervalUnit,
                  uid,
                  viewKey
              ]
          ),
          onChartHover                                  = useCallback(
              ( e : ChartEvent, activeElements : ActiveElement[], chartElement : ChartJS, noUpdate : boolean = false ) => {
                  // not to ignore
                  if (
                      ( e.type === 'mouseout' || !ignoreHover.current )
                      && DSD.current
                  ) {
                      const chartDataSets = chartElement.config.data.datasets,
                            thisDataSets  = DSD.current;

                      let first = true;

                      activeElements = ( [] as ActiveElement[] ).concat( activeElements );

                      // add undefined as activeElement to force restore original colors
                      if ( !activeElements.length ) {
                          activeElements.push( undefined as unknown as ActiveElement );
                      }

                      for ( const activeElement of activeElements ) {
                          const dataSetIndex = activeElement?.datasetIndex,
                                dataIndex    = activeElement?.index;

                          // hover element changed
                          if (
                              dataSetIndex !== lastDataSetIndex.current
                              || dataIndex !== lastDataIndex.current
                          ) {

                              lastDataSetIndex.current = dataSetIndex;
                              lastDataIndex.current = dataIndex;

                              for ( let dataSetCount = 0; dataSetCount < thisDataSets.length; dataSetCount++ ) {
                                  const backgroundColor = thisDataSets[ dataSetCount ].backgroundColor,
                                        borderColor     = thisDataSets[ dataSetCount ].borderColor;

                                  // restore original colors
                                  if ( dataSetIndex === undefined && dataIndex === undefined ) {
                                      if ( chartDataSets[ dataSetCount ].backgroundColor && Array.isArray( chartDataSets[ dataSetCount ].backgroundColor ) ) {
                                          for ( let colorCount = 0; colorCount < ( chartDataSets[ dataSetCount ].backgroundColor as string[] ).length; colorCount++ ) {
                                              ( chartDataSets[ dataSetCount ].backgroundColor as string[] )[ colorCount ] = typeof backgroundColor === 'string'
                                                  ? backgroundColor
                                                  : ( backgroundColor as string[] )[ colorCount ];
                                          }
                                      }
                                      else {
                                          chartDataSets[ dataSetCount ].backgroundColor = backgroundColor;
                                      }
                                      if ( chartDataSets[ dataSetCount ].borderColor && Array.isArray( chartDataSets[ dataSetCount ].borderColor ) ) {
                                          for ( let colorCount = 0; colorCount < ( chartDataSets[ dataSetCount ].borderColor as string[] ).length; colorCount++ ) {
                                              ( chartDataSets[ dataSetCount ].borderColor as string[] )[ colorCount ] = typeof borderColor === 'string'
                                                  ? borderColor
                                                  : ( borderColor as string[] )[ colorCount ];
                                          }
                                      }
                                      else {
                                          chartDataSets[ dataSetCount ].borderColor = borderColor;
                                      }
                                  }

                                  // fade colors of non hovered
                                  else {

                                      if ( Array.isArray( backgroundColor ) ) {
                                          for ( let colorCount = 0; colorCount < backgroundColor.length; colorCount++ ) {

                                              // set hover~Colors to original colors for hovered (remove default hover effect)
                                              if ( dataSetIndex === dataSetCount && dataIndex === colorCount ) {
                                                  ( chartDataSets[ dataSetCount ].backgroundColor as string[] )[ colorCount ] = backgroundColor[ colorCount ];
                                              }

                                              // fade colors of non-hovered
                                              else if (
                                                  first
                                                  && typeof backgroundColor[ colorCount ] === 'string'
                                                  && backgroundColor[ colorCount ].match( /^#[0-9A-F]{6,6}$/i )
                                              ) {
                                                  ( chartDataSets[ dataSetCount ].backgroundColor as string[] )[ colorCount ] = `${backgroundColor[ colorCount ]}55`;
                                              }
                                          }
                                      }
                                      else if (
                                          typeof backgroundColor === 'string'
                                          && backgroundColor?.match( /^#[0-9A-F]{6,6}$/i )
                                      ) {
                                          chartDataSets[ dataSetCount ].backgroundColor = `${backgroundColor}55`;

                                          if ( dataSetCount === dataSetIndex ) {
                                              const data = thisDataSets[ dataSetCount ].data;

                                              if ( data.length === 1 ) {
                                                  chartDataSets[ dataSetCount ].backgroundColor = backgroundColor;
                                              }
                                              else {
                                                  if ( first ) {
                                                      chartDataSets[ dataSetCount ].backgroundColor = [];
                                                  }

                                                  for ( let dataCount = 0; dataCount < data.length; dataCount++ ) {
                                                      if ( dataIndex === dataCount || dataIndex === undefined ) {
                                                          ( chartDataSets[ dataSetCount ].backgroundColor as string[] )[ dataCount ] = backgroundColor;
                                                      }
                                                      else {
                                                          ( chartDataSets[ dataSetCount ].backgroundColor as string[] )[ dataCount ] = `${backgroundColor}55`;
                                                      }
                                                  }
                                              }
                                          }
                                      }

                                      if ( Array.isArray( borderColor ) ) {
                                          for ( let colorCount = 0; colorCount < borderColor.length; colorCount++ ) {

                                              // set hover~Colors to original colors for hovered (remove default hover effect)
                                              if ( dataSetIndex === dataSetCount && dataIndex === colorCount ) {
                                                  ( chartDataSets[ dataSetCount ].borderColor as string[] )[ colorCount ] = borderColor[ colorCount ];
                                              }

                                              // fade colors of non-hovered
                                              else if (
                                                  first
                                                  && typeof borderColor[ colorCount ] === 'string'
                                                  && borderColor[ colorCount ].match( /^#[0-9A-F]{6,6}$/i )
                                              ) {
                                                  ( chartDataSets[ dataSetCount ].borderColor as string[] )[ colorCount ] = `${borderColor[ colorCount ]}55`;
                                              }
                                          }
                                      }
                                      else if (
                                          typeof borderColor === 'string'
                                          && borderColor?.match( /^#[0-9A-F]{6,6}$/i )
                                      ) {
                                          chartDataSets[ dataSetCount ].borderColor = `${borderColor}55`;

                                          if ( dataSetCount === dataSetIndex ) {
                                              const data = thisDataSets[ dataSetCount ].data;

                                              if ( data.length === 1 ) {
                                                  chartDataSets[ dataSetCount ].borderColor = borderColor;
                                              }
                                              else {
                                                  if ( first ) {
                                                      chartDataSets[ dataSetCount ].borderColor = [];
                                                  }

                                                  for ( let dataCount = 0; dataCount < data.length; dataCount++ ) {
                                                      if ( dataIndex === dataCount || dataIndex === undefined ) {
                                                          ( chartDataSets[ dataSetCount ].borderColor as string[] )[ dataCount ] = borderColor;
                                                      }
                                                      else {
                                                          ( chartDataSets[ dataSetCount ].borderColor as string[] )[ dataCount ] = `${borderColor}55`;
                                                      }
                                                  }
                                              }
                                          }
                                      }
                                  }
                              }

                              if ( !noUpdate ) {
                                  chartElement.update();
                              }
                          }
                          else {
                              break;
                          }

                          first = false;
                      }
                  }

                  if ( e.type === 'mouseout' ) {
                      ignoreHover.current = true;
                  }
                  else {
                      ignoreHover.current = false;
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onChartLeave                                  = useCallback(
              ( e : Event ) => {
                  const chartJs = chart.current?.getChart();

                  if ( chartJs ) {
                      ignoreHover.current = true;
                      onChartHover(
                          {
                              type  : 'mouseout',
                              native: e,
                              x     : null,
                              y     : null
                          },
                          [],
                          chartJs
                      );
                  }
              },
              [
                  onChartHover
              ]
          ),
          onLegendItemClick                             = useCallback(
              ( legendIndex : number ) => {
                  if ( elusiveData?.legendItems ) {
                      const newLegendItems = ( [] as LegendItem[] ).concat( elusiveData.legendItems );

                      newLegendItems[ legendIndex ] = {
                          ...newLegendItems[ legendIndex ]
                      };
                      newLegendItems[ legendIndex ].hidden = !newLegendItems[ legendIndex ].hidden;

                      dispatch( setElusiveData( uid, { legendItems: newLegendItems } ) );
                  }
              },
              [
                  elusiveData?.legendItems
              ]
          ),
          onLegendItemHover                             = useCallback(
              ( legendIndex : number, e? : React.MouseEvent<HTMLDivElement> ) => {
                  const chartJs = chart.current?.getChart();

                  if ( chartJs && elusiveData?.legendItems ) {
                      const legendItem = elusiveData.legendItems[ legendIndex ];

                      if ( !legendItem.hidden ) {
                          const activeDataPoints : ActiveDataPoint[] = [],
                                dataSet                              = elusiveData?.dataSetData?.[ legendItem.dataSetIndex ],
                                dataSetType                          = dataSet?.type ?? type as ChartJSType;

                          if ( legendItem.dataIndex !== undefined ) {
                              activeDataPoints.push( {
                                  datasetIndex: legendItem.dataSetIndex,
                                  index       : legendItem.dataIndex
                              } );
                          }
                          else {
                              for ( let index = 0; index < chartJs.config.data.datasets[ legendItem.dataSetIndex ].data.length; index++ ) {
                                  activeDataPoints.push( {
                                      datasetIndex: legendItem.dataSetIndex,
                                      index
                                  } );
                              }
                          }

                          chartJs.setActiveElements( activeDataPoints );

                          // tooltip
                          chartJs.tooltip.setActiveElements(
                              activeDataPoints,
                              {
                                  x: ( chartJs.chartArea.left + chartJs.chartArea.right ) / 2,
                                  y: ( chartJs.chartArea.top + chartJs.chartArea.bottom ) / 2
                              }
                          );

                          // hover
                          ignoreHover.current = false;
                          onChartHover(
                              {
                                  type  : 'mouseenter',
                                  native: e as unknown as Event,
                                  x     : null,
                                  y     : null
                              },
                              chartJs.getActiveElements(),
                              chartJs,
                              dataSet && dataSetType === 'line' // noUpdate
                          );

                          // special for line
                          if ( dataSet && dataSetType === 'line' ) {
                              chartJs.config.data.datasets[ legendItem.dataSetIndex ].backgroundColor = dataSet.backgroundColor;
                              chartJs.config.data.datasets[ legendItem.dataSetIndex ].borderColor = dataSet.borderColor;
                              chartJs.config.data.datasets[ legendItem.dataSetIndex ].pointHoverRadius = 1;
                              chartJs.config.data.datasets[ legendItem.dataSetIndex ].borderWidth = 2;
                              chartJs.update();
                          }
                      }
                  }
              },
              [
                  elusiveData?.dataSetData,
                  elusiveData?.legendItems,
                  onChartHover
              ]
          ),
          onLegendItemLeave                             = useCallback(
              ( legendIndex : number, e? : React.MouseEvent<HTMLDivElement> ) => {
                  const chartJs = chart.current?.getChart();

                  if ( chartJs ) {
                      const legendItem  = elusiveData?.legendItems?.[ legendIndex ],
                            dataSet     = legendItem ? elusiveData?.dataSetData?.[ legendItem.dataSetIndex ] : undefined,
                            dataSetType = dataSet?.type ?? type as ChartJSType;

                      chartJs.setActiveElements( [] );

                      chartJs.tooltip.setActiveElements(
                          [],
                          {
                              x: 0,
                              y: 0
                          }
                      );

                      ignoreHover.current = true;
                      onChartHover(
                          {
                              type  : 'mouseout',
                              native: e as unknown as Event,
                              x     : null,
                              y     : null
                          },
                          [],
                          chartJs,
                          legendItem && dataSet && dataSetType === 'line' // noUpdate
                      );

                      // special for line
                      if ( legendItem && dataSet && dataSetType === 'line' ) {
                          delete chartJs.config.data.datasets[ legendItem.dataSetIndex ].pointHoverRadius;
                          delete chartJs.config.data.datasets[ legendItem.dataSetIndex ].borderWidth;
                          chartJs.update();
                      }
                  }
              },
              [
                  elusiveData?.dataSetData,
                  elusiveData?.legendItems,
                  onChartHover
              ]
          ),
          options                                       = useMemo(
              () => {
                  const retVal : ChartOptions = {
                            parsing    : false,
                            plugins    : {
                                legend : {
                                    display: false
                                },
                                tooltip: {
                                    position       : 'nearest',
                                    usePointStyle  : true,
                                    bodyFont       : {
                                        family: 'monospace',
                                        size  : 10
                                    },
                                    backgroundColor: COLOR_SCHEME_COLORS[ colorScheme ].tooltipBackground,
                                    titleColor     : COLOR_SCHEME_COLORS[ colorScheme ].tooltipText,
                                    // @ts-expect-error - ignore
                                    headerColor: COLOR_SCHEME_COLORS[ colorScheme ].tooltipText,
                                    bodyColor  : COLOR_SCHEME_COLORS[ colorScheme ].tooltipText,
                                    footerColor: COLOR_SCHEME_COLORS[ colorScheme ].tooltipText
                                }
                            },
                            responsive : true,
                            interaction: {
                                intersect: false
                            }
                        },
                        decimalsDelimiter     = Intl.NumberFormat().format( 1.1 ).substring( 1, 2 );

                  // scales
                  if ( scales?.length ) {
                      if ( !retVal.scales ) {
                          retVal.scales = {};
                      }

                      for ( const scale of scales ) {
                          const scaleId = scale.data.key;

                          let position : 'left' | 'right' | 'center' | 'top' | 'bottom' | undefined;

                          if ( scale.data.axis === 'x' ) {
                              if ( scale.data.position === 'start' ) {
                                  position = 'bottom';
                              }
                              else if ( scale.data.position === 'end' ) {
                                  position = 'top';
                              }
                              else if ( scale.data.position ) {
                                  position = scale.data.position;
                              }
                          }
                          else if ( scale.data.axis === 'y' ) {
                              if ( scale.data.position === 'start' ) {
                                  position = 'left';
                              }
                              else if ( scale.data.position === 'end' ) {
                                  position = 'right';
                              }
                              else if ( scale.data.position ) {
                                  position = scale.data.position;
                              }
                          }

                          retVal.scales[ scaleId ] = {
                              axis        : scale.data.axis,
                              position,
                              max         : scale.data.max,
                              min         : scale.data.min,
                              suggestedMax: scale.data.suggestedMax,
                              suggestedMin: scale.data.suggestedMin,
                              ticks       : {
                                  stepSize: scale.data.stepSize
                              }
                          };

                          // scale type
                          if ( scale.data.type ) {
                              if ( type === 'bar' || type === 'line' ) {
                                  retVal.scales[ scaleId ]!.type = scale.data.type;
                              }
                              else if ( scale.data.type === 'linear' && ( type === 'polarArea' || type === 'radar' ) ) {
                                  retVal.scales[ scaleId ]!.type = 'radialLinear';
                              }
                          }

                          // scale unit
                          if ( scale.data.unit ) {
                              retVal.scales[ scaleId ]!.ticks = {
                                  callback: ( value ) => {
                                      return `${value} ${scale.data.unit}`;
                                  }
                              };
                          }

                          // scale label
                          if ( scale.messages?.title ) {
                              if ( type === 'bar' || type === 'line' ) {
                                  ( retVal.scales as ( ChartJSOptions<'bar'> | ChartJSOptions<'line'> )['scales'] )![ scaleId ]!.title = {
                                      display: true,
                                      text   : __( typeof scale.messages.title === 'string' ? scale.messages.title : scale.messages.title.key )
                                  }
                              }
                          }

                          if ( !retVal.scales[ scale.data.axis ] ) {
                              retVal.scales[ scale.data.axis ] = retVal.scales[ scaleId ];
                              delete retVal.scales[ scaleId ];
                          }
                      }
                  }

                  if ( type === 'bar' ) {
                      let first : boolean     = true,
                          isDataSet : boolean = false,
                          maxSignificantChars : number | undefined,
                          maxFractionLength : number | undefined,
                          maxLabelLength : number | undefined;

                      retVal.onHover = onChartHover;

                      retVal.interaction!.mode = 'x';
                      retVal.plugins!.tooltip!.callbacks = {
                          title          : ( items : TooltipItem<any>[] ) => {
                              first = true;
                              maxSignificantChars = undefined;
                              maxFractionLength = undefined;
                              maxLabelLength = undefined;

                              if ( items.length === 1 ) {
                                  isDataSet = false;
                              }
                              else {
                                  isDataSet = true;

                                  for ( const item of items ) {
                                      const itemValue                    = item.formattedValue === Intl.NumberFormat().format( item.parsed.y )
                                                ? item.parsed.y
                                                : item.parsed.x,
                                            itemStringValue              = String( itemValue ),
                                            itemValueMaxSignificantChars = Intl.NumberFormat().format( parseInt( itemValue ) ).length;

                                      let itemValueMaxFractionLength : number | undefined;

                                      if ( maxSignificantChars === undefined ) {
                                          maxSignificantChars = itemValueMaxSignificantChars;
                                      }
                                      else {
                                          maxSignificantChars = itemValueMaxSignificantChars > maxSignificantChars ? itemValueMaxSignificantChars : maxSignificantChars;
                                      }

                                      if ( itemStringValue.indexOf( '.' ) !== -1 ) {
                                          itemValueMaxFractionLength = String( itemValue ).substring( String( itemValue ).indexOf( '.' ) + 1 ).length
                                      }

                                      if ( itemValueMaxFractionLength !== undefined ) {
                                          if ( maxFractionLength === undefined ) {
                                              maxFractionLength = itemValueMaxFractionLength;
                                          }
                                          else {
                                              maxFractionLength = itemValueMaxFractionLength > maxFractionLength ? itemValueMaxFractionLength : maxFractionLength;
                                          }
                                      }

                                      if ( maxLabelLength === undefined ) {
                                          maxLabelLength = item.label.length;
                                      }
                                      else {
                                          maxLabelLength = item.label.length > maxLabelLength ? item.label.length : maxLabelLength;
                                      }
                                  }
                              }

                              if ( dataSets?.length === 1 ) {
                                  return '';
                              }
                              else {
                                  return legendItems[ items[ 0 ].datasetIndex ]?.label ?? '';
                              }
                          },
                          label          : ( item : TooltipItem<any> ) => {
                              if ( isDataSet ) {
                                  const itemValue = item.formattedValue === Intl.NumberFormat().format( item.parsed.y )
                                      ? item.parsed.y
                                      : item.parsed.x;

                                  let label = item.label,
                                      value = item.formattedValue;

                                  if ( maxFractionLength !== undefined && maxFractionLength > 0 ) {
                                      if ( value.indexOf( decimalsDelimiter ) === -1 ) {
                                          value += decimalsDelimiter + '0'.repeat( maxFractionLength );
                                      }
                                      else {
                                          value += '0'.repeat( maxFractionLength - value.length + value.indexOf( decimalsDelimiter ) + 1 );
                                      }
                                  }

                                  if ( maxSignificantChars !== undefined ) {
                                      value = ' '.repeat( maxSignificantChars - Intl.NumberFormat().format( parseInt( itemValue ) ).length ) + value;
                                  }

                                  if ( maxLabelLength !== undefined ) {
                                      label += ' '.repeat( maxLabelLength - label.length );
                                  }

                                  return `${label}: ${value}`;
                              }
                              else {
                                  return item.formattedValue;
                              }
                          },
                          labelPointStyle: () => {
                              if ( first ) {
                                  first = false;

                                  return;
                              }
                              else {
                                  return {
                                      pointStyle: false as PointStyle,
                                      rotation  : 0
                                  }
                              }
                          }
                      };
                  }
                  else if ( type === 'doughnut' ) {
                      retVal.onHover = onChartHover;
                  }
                  else if ( type === 'pie' ) {
                      retVal.onHover = onChartHover;
                  }
                  else if ( type === 'polarArea' ) {
                      retVal.onHover = onChartHover;
                  }
                  else if ( type === 'radar' || type === 'line' ) {
                      let first : boolean     = true,
                          isDataSet : boolean = false,
                          maxSignificantChars : number | undefined,
                          maxFractionLength : number | undefined,
                          maxLabelLength : number | undefined,
                          scaleUnit : string | undefined;

                      retVal.plugins!.tooltip!.callbacks = {
                          title          : ( items : TooltipItem<any>[] ) => {
                              first = true;
                              maxSignificantChars = undefined;
                              maxFractionLength = undefined;
                              maxLabelLength = undefined;
                              scaleUnit = undefined;

                              if ( items.length === 1 ) {
                                  isDataSet = false;
                              }
                              else {
                                  isDataSet = true;

                                  for ( const item of items ) {
                                      const itemValue                    = item.formattedValue === Intl.NumberFormat().format( item.parsed.y )
                                                ? item.parsed.y
                                                : item.parsed.x,
                                            itemStringValue              = String( itemValue ),
                                            itemValueMaxSignificantChars = Intl.NumberFormat().format( parseInt( itemValue ) ).length;

                                      let itemValueMaxFractionLength : number | undefined;

                                      if ( maxSignificantChars === undefined ) {
                                          maxSignificantChars = itemValueMaxSignificantChars;
                                      }
                                      else {
                                          maxSignificantChars = itemValueMaxSignificantChars > maxSignificantChars ? itemValueMaxSignificantChars : maxSignificantChars;
                                      }

                                      if ( itemStringValue.indexOf( '.' ) !== -1 ) {
                                          itemValueMaxFractionLength = String( itemValue ).substring( String( itemValue ).indexOf( '.' ) + 1 ).length
                                      }

                                      if ( itemValueMaxFractionLength !== undefined ) {
                                          if ( maxFractionLength === undefined ) {
                                              maxFractionLength = itemValueMaxFractionLength;
                                          }
                                          else {
                                              maxFractionLength = itemValueMaxFractionLength > maxFractionLength ? itemValueMaxFractionLength : maxFractionLength;
                                          }
                                      }

                                      if ( maxLabelLength === undefined ) {
                                          maxLabelLength = item.label.length;
                                      }
                                      else {
                                          maxLabelLength = item.label.length > maxLabelLength ? item.label.length : maxLabelLength;
                                      }
                                  }
                              }

                              if ( scaleUnit === undefined ) {
                                  const dataSet = dataSets?.[ items[ 0 ].datasetIndex ],
                                        scaleId = dataSet?.scale,
                                        scale   = scaleId
                                            ? scales?.find( s => s.data.key === scaleId )
                                            : undefined;

                                  scaleUnit = scale?.data.unit ?? '';
                              }

                              if ( dataSets?.length === 1 ) {
                                  return '';
                              }
                              else {
                                  return legendItems[ items[ 0 ].datasetIndex ]?.label ?? '';
                              }
                          },
                          label          : ( item : TooltipItem<any> ) => {
                              if ( isDataSet ) {
                                  const itemValue = item.formattedValue === Intl.NumberFormat().format( item.parsed.y )
                                      ? item.parsed.y
                                      : item.parsed.x;

                                  let label = item.label,
                                      value = item.formattedValue;

                                  if ( maxFractionLength !== undefined && maxFractionLength > 0 ) {
                                      if ( value.indexOf( decimalsDelimiter ) === -1 ) {
                                          value += decimalsDelimiter + '0'.repeat( maxFractionLength );
                                      }
                                      else {
                                          value += '0'.repeat( maxFractionLength - value.length + value.indexOf( decimalsDelimiter ) + 1 );
                                      }
                                  }

                                  if ( maxSignificantChars !== undefined ) {
                                      value = ' '.repeat( maxSignificantChars - Intl.NumberFormat().format( parseInt( itemValue ) ).length ) + value;
                                  }

                                  if ( maxLabelLength !== undefined ) {
                                      label += ' '.repeat( maxLabelLength - label.length );
                                  }

                                  return `${label}: ${value}${scaleUnit ? ` ${scaleUnit}` : ''}`;
                              }
                              else {
                                  return item.formattedValue;
                              }
                          },
                          labelPointStyle: () => {
                              if ( first ) {
                                  first = false;

                                  return;
                              }
                              else {
                                  return {
                                      pointStyle: false as PointStyle,
                                      rotation  : 0
                                  }
                              }
                          }
                      };
                  }

                  // time series specific
                  if ( isTimeSeries ) {
                      if ( !retVal.scales ) {
                          retVal.scales = {};
                      }

                      retVal.scales.x = {
                          type    : 'time',
                          time    : {
                              unit: timeSeriesIntervalUnit,
                              // moment format string
                              tooltipFormat : __( `YN.base.common.ChartJS.time.displayFormats.${timeSeriesIntervalUnit}` ),
                              isoWeekday    : Number( __( 'YN.base.common.ChartJS.time.isoWeekday' ) ),
                              displayFormats: {
                                  millisecond: __( 'YN.base.common.ChartJS.time.displayFormats.millisecond' ),
                                  second     : __( 'YN.base.common.ChartJS.time.displayFormats.second' ),
                                  minute     : __( 'YN.base.common.ChartJS.time.displayFormats.minute' ),
                                  //hour     : __( 'YN.base.common.ChartJS.time.displayFormats.hour' ),
                                  day    : __( 'YN.base.common.ChartJS.time.displayFormats.day' ),
                                  week   : __( 'YN.base.common.ChartJS.time.displayFormats.week' ),
                                  month  : __( 'YN.base.common.ChartJS.time.displayFormats.month' ),
                                  quarter: __( 'YN.base.common.ChartJS.time.displayFormats.quarter' )
                                  //year    : __( 'YN.base.common.ChartJS.time.displayFormats.year' )
                              }
                          }
                      };

                      retVal.elements = {
                          point: {
                              radius: 1
                          },
                          line : {
                              borderWidth: 1
                          }
                      };

                      let lastTooltipFormattedValue : string | undefined,
                          lastTooltipDataIndex : number | undefined,
                          lastTooltipDataSetIndex : number | undefined,
                          first : boolean     = true,
                          isDataSet : boolean = false,
                          maxSignificantChars : number | undefined,
                          maxFractionLength : number | undefined,
                          maxLabelLength : number | undefined,
                          scaleUnit : string | undefined;

                      retVal.plugins!.tooltip!.callbacks = {
                          title: ( items : TooltipItem<any>[] ) => {
                              first = true;
                              maxSignificantChars = undefined;
                              maxFractionLength = undefined;
                              maxLabelLength = undefined;
                              scaleUnit = undefined;

                              if ( items.length === 1 ) {
                                  isDataSet = false;
                              }
                              else {
                                  isDataSet = true;

                                  for ( const item of items ) {
                                      const itemValue                    = item.formattedValue === Intl.NumberFormat().format( item.parsed.y )
                                                ? item.parsed.y
                                                : item.parsed.x,
                                            itemStringValue              = String( itemValue ),
                                            itemValueMaxSignificantChars = Intl.NumberFormat().format( parseInt( itemValue ) ).length;

                                      let itemValueMaxFractionLength : number | undefined;

                                      if ( maxSignificantChars === undefined ) {
                                          maxSignificantChars = itemValueMaxSignificantChars;
                                      }
                                      else {
                                          maxSignificantChars = itemValueMaxSignificantChars > maxSignificantChars ? itemValueMaxSignificantChars : maxSignificantChars;
                                      }

                                      if ( itemStringValue.indexOf( '.' ) !== -1 ) {
                                          itemValueMaxFractionLength = String( itemValue ).substring( String( itemValue ).indexOf( '.' ) + 1 ).length
                                      }

                                      if ( itemValueMaxFractionLength !== undefined ) {
                                          if ( maxFractionLength === undefined ) {
                                              maxFractionLength = itemValueMaxFractionLength;
                                          }
                                          else {
                                              maxFractionLength = itemValueMaxFractionLength > maxFractionLength ? itemValueMaxFractionLength : maxFractionLength;
                                          }
                                      }

                                      if ( maxLabelLength === undefined ) {
                                          maxLabelLength = item.label.length;
                                      }
                                      else {
                                          maxLabelLength = item.label.length > maxLabelLength ? item.label.length : maxLabelLength;
                                      }
                                  }
                              }

                              if ( scaleUnit === undefined ) {
                                  const dataSet = dataSets?.[ items[ 0 ].datasetIndex ],
                                        scaleId = dataSet?.scale,
                                        scale   = scaleId
                                            ? scales?.find( s => s.data.key === scaleId )
                                            : undefined;

                                  scaleUnit = scale?.data.unit ?? '';
                              }

                              return legendItems[ items[ 0 ].datasetIndex ]?.label ?? '';
                          },
                          label: ( item : TooltipItem<any> ) => {
                              if ( item.dataIndex === 0 || item.datasetIndex !== lastTooltipDataSetIndex ) {
                                  lastTooltipFormattedValue = undefined;
                                  lastTooltipDataIndex = undefined;
                              }
                              lastTooltipDataSetIndex = item.datasetIndex;

                              if ( !isDataSet || item.formattedValue !== lastTooltipFormattedValue || item.dataIndex === item.dataset.data.length - 1 ) {
                                  const itemValue = item.formattedValue === Intl.NumberFormat().format( item.parsed.y )
                                      ? item.parsed.y
                                      : item.parsed.x;

                                  let label = item.label,
                                      value = item.formattedValue;

                                  lastTooltipFormattedValue = item.formattedValue;
                                  lastTooltipDataIndex = item.dataIndex;

                                  if ( maxFractionLength !== undefined && maxFractionLength > 0 ) {
                                      if ( value.indexOf( decimalsDelimiter ) === -1 ) {
                                          value += decimalsDelimiter + '0'.repeat( maxFractionLength );
                                      }
                                      else {
                                          value += '0'.repeat( maxFractionLength - value.length + value.indexOf( decimalsDelimiter ) + 1 );
                                      }
                                  }

                                  if ( maxSignificantChars !== undefined ) {
                                      value = ' '.repeat( maxSignificantChars - Intl.NumberFormat().format( parseInt( itemValue ) ).length ) + value;
                                  }

                                  if ( maxLabelLength !== undefined ) {
                                      label += ' '.repeat( maxLabelLength - label.length );
                                  }

                                  return `${label}: ${value}${scaleUnit ? ` ${scaleUnit}` : ''}`;
                              }
                              else if ( item.dataIndex === ( lastTooltipDataIndex ?? 0 ) + 1 ) {
                                  return '  ⋮';
                              }
                              else {
                                  return '';
                              }
                          },
                          labelColor( item : TooltipItem<any> ) : TooltipLabelStyle | void {
                              if ( first ) {
                                  const color = item.chart.data.datasets[ item.datasetIndex ].borderColor;

                                  return {
                                      backgroundColor: color as Color,
                                      borderColor    : 'transparent'
                                  };
                              }
                              else {
                                  return;
                              }
                          },
                          labelPointStyle: () => {
                              if ( first ) {
                                  first = false;

                                  return;
                              }
                              else {
                                  return {
                                      pointStyle: false as PointStyle,
                                      rotation  : 0
                                  }
                              }
                          }
                      }
                  }

                  // dataSet specific
                  if ( dataSets ) {
                      for ( const dataSet of dataSets ) {
                          const dataSetType = dataSet.data.type ?? type as ChartJSType;

                          // type 'bar' specific
                          if ( dataSetType === 'bar' ) {
                              let delayed = false;

                              retVal.animation = {
                                  onComplete: () => {
                                      delayed = true;
                                  },
                                  delay     : ( context : {
                                      dataIndex : number;
                                      datasetIndex : number;
                                      type : string;
                                      mode : string;
                                  } ) => {
                                      let delay = 0;

                                      if ( context.type === 'data' && context.mode === 'default' && !delayed ) {
                                          delay = context.datasetIndex * 100 + context.dataIndex * ( 100 * dataSets.length );
                                      }

                                      return delay;
                                  }
                              };

                              if ( dataSet.data.horizontal ) {
                                  retVal.indexAxis = 'y';
                                  retVal.interaction!.mode = 'y';
                              }
                              // TODO: not working
                              // TODO: only for dataSets.length === 1
                              /*else {
                                  retVal.transitions = {
                                      show: {
                                          animations: {
                                              x: {
                                                  from: ( options ) => {
                                                      return options.chart.chartArea.right;
                                                  }
                                              }
                                          }
                                      },
                                      hide: {
                                          animations: {
                                              width : {
                                                  from: ( options ) => {
                                                      return options.element ? options.element.width : 0;
                                                  },
                                                  to  : 0
                                              },
                                              height: {
                                                  from: ( options ) => {
                                                      return options.element ? options.element.height : 0;
                                                  },
                                                  to  : 0
                                              },
                                              x     : {
                                                  from: ( options ) => {
                                                      return options.element ? options.element.x : 0;
                                                  },
                                                  to  : ( options ) => {
                                                      return options.chart.chartArea.right + ( options.element ? options.element.width : 0 );
                                                  }
                                              }
                                          }
                                      }
                                  };
                              }*/
                          }

                          // type 'bar' and 'line' specific
                          if ( type === 'bar' || type === 'line' ) {

                              // stacked
                              if ( dataSet.data.stackGroup ) {
                                  if ( !retVal.scales ) {
                                      retVal.scales = {};
                                  }

                                  for ( const scale of Object.keys( retVal.scales ) ) {
                                      if ( retVal.scales[ scale ]!.type !== 'category' && retVal.scales[ scale ]!.type !== 'radialLinear' ) {
                                          ( retVal.scales as ( ChartJSOptions<'bar'> | ChartJSOptions<'bubble'> | ChartJSOptions<'line'> | ChartJSOptions<'scatter'> )['scales'] )![ scale ]!.stacked = true;
                                      }
                                  }

                                  retVal.scales.x = {
                                      ...retVal.scales.x,
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-expect-error
                                      stacked: true
                                  };
                                  retVal.scales.y = {
                                      ...retVal.scales.y,
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-expect-error
                                      stacked: true
                                  };
                              }
                          }
                      }
                  }

                  return retVal;
              },
              [
                  colorScheme,
                  dataSets,
                  isTimeSeries,
                  language,
                  legendAlign,
                  legendPosition,
                  messageCatalogTimestamp,
                  onChartHover,
                  scales,
                  timeSeriesIntervalUnit,
                  type,
                  xAxisLabelDataDescriptor
              ]
          ),
          selectors                                     = useMemo(
              () => {
                  let retVal : React.ReactNode = null;

                  if ( timeSeriesIntervalUnit && ( timeSeriesShowIntervalSelector || timeSeriesShowTimeRangeSelector ) ) {
                      const selector : React.ReactNode[] = [];

                      if ( timeSeriesShowTimeRangeSelector ) {
                          const valueType = [ 'day', 'week', 'month', 'quarter', 'year' ].includes( timeSeriesIntervalUnit ) ? 'DATE' : 'TIMESTAMP';

                          selector.push(
                              <div key="range" className="yn-chart-selector yn-chart-selector-range">
                                  <div className="yn-chart-selector-label">{__( 'YN.base.frontend.Chart.selector.range' )}</div>
                                  <div className="yn-chart-selector-field">
                                      <FilterField
                                          fieldKey={`${uid}_timeSeriesIntervalStart`}

                                          value={timeSeriesIntervalStart}
                                          onChange={( key : string, field : string, matchMode : FilterMatchMode, fieldValue : any ) => {
                                              dispatch( setElusiveData( uid, { timeSeriesIntervalStart: `${fieldValue}${valueType === 'DATE' ? 'T00:00:00.000Z' : ''}` } ) );
                                          }}

                                          valueType={valueType}
                                          field="expression"
                                          adjustWidth={false}
                                          showMatchModeSelector={false}
                                          showMatchField={true}
                                          hasCustomMatchMode={false}
                                          markDirty={false}
                                          classNames={[ 'yn-chart-selector-field-range-start' ]}
                                      />
                                      <FilterField
                                          fieldKey={`${uid}_timeSeriesIntervalEnd`}

                                          value={timeSeriesIntervalEnd}
                                          onChange={( key : string, field : string, matchMode : FilterMatchMode, fieldValue : any ) => {
                                              dispatch( setElusiveData( uid, { timeSeriesIntervalEnd: `${fieldValue}${valueType === 'DATE' ? 'T00:00:00.000Z' : ''}` } ) );
                                          }}

                                          valueType={valueType}
                                          field="expression"
                                          adjustWidth={false}
                                          showMatchModeSelector={false}
                                          showMatchField={true}
                                          hasCustomMatchMode={false}
                                          markDirty={false}
                                          classNames={[ 'yn-chart-selector-field-range-end' ]}
                                      />
                                  </div>
                              </div>
                          );
                      }

                      if ( timeSeriesShowIntervalSelector ) {
                          selector.push(
                              <div key="interval" className="yn-chart-selector yn-chart-selector-interval">
                                  <div className="yn-chart-selector-label">{__( 'YN.base.frontend.Chart.selector.interval' )}</div>
                                  <div className="yn-chart-selector-field">
                                      <InputNumber
                                          id={`${uid}_timeSeriesIntervalCount`}

                                          value={timeSeriesIntervalCount}
                                          onChange={( event ) => {
                                              dispatch( setElusiveData( uid, { timeSeriesIntervalCount: event.value ? event.value : 1 } ) );
                                          }}

                                          useGrouping={false}
                                          mode="decimal"
                                          min={1}
                                          step={1}

                                          className="yn-chart-selector-field-interval-count"
                                      />
                                      <ValueSetField
                                          uid={`${uid}_timeSeriesIntervalUnit`}

                                          value={timeSeriesIntervalUnit}
                                          onChange={( fieldValue : AttributeValue | undefined ) => {
                                              dispatch( setElusiveData( uid, { timeSeriesIntervalUnit: fieldValue as TimeSeriesIntervalUnit } ) );
                                          }}

                                          schema="CFG"
                                          valueSetId={timeSeriesIntervalUnitValueSetId}
                                          allowNone={false}

                                          classNames={[ 'yn-chart-selector-field-interval-unit' ]}
                                          appendToElementId="document"
                                          infoTextLayout="hidden"
                                          labelVisibility="hidden"
                                      />
                                  </div>
                              </div>
                          );
                      }

                      // apply button
                      selector.push(
                          <div key="button" className="yn-chart-selector yn-chart-selector-button">
                              <div className="yn-chart-selector-label">&nbsp;</div>
                              <div className="yn-chart-selector-field">
                                  <Button
                                      onClick={() => {
                                          const timeSeriesParameters : TimeSeriesParameters = {
                                              timeSeriesIntervalCount: timeSeriesIntervalUnit ? timeSeriesIntervalCount : undefined,
                                              timeSeriesIntervalEnd  : timeSeriesIntervalUnit ? timeSeriesIntervalEnd : undefined,
                                              timeSeriesIntervalStart: timeSeriesIntervalUnit ? timeSeriesIntervalStart : undefined,
                                              timeSeriesIntervalUnit : timeSeriesIntervalUnit ? timeSeriesIntervalUnit : undefined
                                          };

                                          if ( onBeforeReloadData?.( timeSeriesParameters ) ) {
                                              void load();
                                          }
                                      }}

                                      label={__( 'YN.base.common.apply' )}

                                      text={false}
                                      outlined={true}
                                      className="yn-chart-selector-field-button"
                                  />
                              </div>
                          </div>
                      );

                      retVal = <div className="yn-chart-selectors">
                          {selector}
                      </div>;
                  }

                  return retVal;
              },
              [
                  language,
                  messageCatalogTimestamp,
                  onBeforeReloadData,
                  timeSeriesIntervalCount,
                  timeSeriesIntervalEnd,
                  timeSeriesIntervalStart,
                  timeSeriesIntervalUnit,
                  timeSeriesIntervalUnitValueSetId,
                  timeSeriesShowIntervalSelector,
                  timeSeriesShowTimeRangeSelector,
                  uid
              ]
          ),
          legend                                        = useMemo(
              () => {
                  const retVal : React.ReactNode[] = [];

                  if ( elusiveData?.legendItems ) {
                      for ( let legendCount = 0; legendCount < elusiveData.legendItems.length; legendCount++ ) {
                          const legendItemsItem = elusiveData.legendItems[ legendCount ];

                          retVal.push(
                              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                              <div
                                  key={`${elementId}_${legendCount}`}
                                  className={`yn-chart-legend-item ${legendItemsItem.hidden ? 'yn-chart-legend-item-hidden' : ''}`}
                                  onClick={onLegendItemClick.bind( null, legendCount )}
                                  onMouseEnter={onLegendItemHover.bind( null, legendCount )}
                                  onMouseLeave={onLegendItemLeave.bind( null, legendCount )}
                              >
                                  <div className="yn-chart-legend-item-color"
                                       style={{ backgroundColor: legendItemsItem.backgroundColor, borderColor: legendItemsItem.borderColor }}></div>
                                  <div className="yn-chart-legend-item-label">{legendItemsItem.label}</div>
                              </div>
                          );
                      }
                  }

                  return retVal;
              },
              [
                  elusiveData?.legendItems,
                  onLegendItemClick,
                  onLegendItemHover,
                  onLegendItemLeave
              ]
          ),
          chartContent                                  = useMemo(
              () => {
                  const datasets = JSON.parse( JSON.stringify( ( [] as ChartDataSetData[] ).concat( elusiveData?.dataSetData ?? [] ) ) );

                  return <PrimeChart
                      ref={chart}
                      id={elementId}
                      type={type}
                      data={{
                          labels: elusiveData?.xAxisValues,
                          datasets
                      }}
                      options={elusiveData?.options}
                      plugins={type === 'bar' || type === 'doughnut' || type === 'pie' || type === 'polarArea'
                          ? [
                              //DatasetShadow, // disabled due to performance issues
                              TooltipShadow
                          ]
                          : [
                              TooltipShadow
                          ]
                      }
                      pt={{
                          canvas: {
                              onMouseOut  : onChartLeave,
                              onMouseLeave: onChartLeave
                          }
                      }}
                  />;
              },
              [
                  elementId,
                  elusiveData?.dataSetData,
                  elusiveData?.options,
                  elusiveData?.xAxisValues,
                  onChartLeave,
                  //type, // do not uncomment since 'old' elusiveData with 'new' type can cause errors -> elusiveData will change on type change (but one render cycle later)
                  uid
              ]
          ),
          content                                       = useMemo(
              () => {
                  return <div
                      id={`${uid}_chart`}
                      className={`yn-chart ${legendClassNames} ${limitToHeight ? 'yn-chart-limit-to-height' : ''}`}
                      style={{
                          height: limitToHeight && ( legendPosition === 'top' || legendPosition === 'bottom' ) ? '100%' : undefined,
                          width : limitToHeight && ( legendPosition === 'left' || legendPosition === 'right' ) ? '100%' : undefined
                      }}
                  >
                      {selectors}
                      <div className="yn-chart-legend">
                          {legend}
                      </div>
                      <div
                          className="yn-chart-wrapper"
                          style={{
                              height: limitToHeight ? chartWrapperHeight : undefined,
                              width : limitToHeight ? chartWrapperWidth : undefined
                          }}
                      >
                          {chartContent}
                      </div>
                  </div>;
              },
              [
                  chartContent,
                  chartWrapperHeight,
                  chartWrapperWidth,
                  legend,
                  legendClassNames,
                  legendPosition,
                  limitToHeight,
                  onChartLeave,
                  selectors
              ]
          );

    DSD.current = elusiveData?.dataSetData;

    // ( on mount / unmount )
    // add/remove listener for color scheme switch
    useEffect(
        () => {
            const matchMediaChangeHandler = () => {
                setColorScheme( getColorScheme() );
            };

            matchMedia( '(prefers-color-scheme: dark)' ).addEventListener( 'change', matchMediaChangeHandler );

            // unmount
            return () => {
                matchMedia( '(prefers-color-scheme: dark)' ).removeEventListener( 'change', matchMediaChangeHandler );
            }
        },
        []
    );

    // ( on update )
    // resize chart
    useLayoutEffect(
        () => {
            const chartJs = chart.current?.getChart();

            if ( limitToHeight ) {
                const chartElement = document.querySelector( `#${uid}_chart` );

                if ( chartElement ) {
                    const legendElement    = document.querySelector( `#${uid}_chart .yn-chart-legend` ),
                          selectorElement  = document.querySelector( `#${uid}_chart .yn-chart-selectors` ),
                          chartParentWidth = chartElement.parentElement?.clientWidth;

                    let newChartWrapperHeight = 0,
                        newChartWrapperWidth  = 0,
                        legendWidth           = 0;

                    newChartWrapperHeight = chartElement.clientHeight;

                    if ( legendElement ) {
                        if ( legendPosition === 'bottom' || legendPosition === 'top' ) {
                            newChartWrapperHeight -= legendElement.clientHeight;
                        }
                        else if ( legendPosition === 'left' || legendPosition === 'right' ) {
                            legendWidth = legendElement.clientWidth;
                        }
                    }

                    if ( selectorElement ) {
                        newChartWrapperHeight -= selectorElement.clientHeight;
                    }

                    if ( type === 'doughnut' || type === 'pie' || type === 'polarArea' || type === 'radar' ) {
                        newChartWrapperWidth = newChartWrapperHeight;
                    }
                    else {
                        newChartWrapperWidth = 2 * newChartWrapperHeight;
                    }

                    if ( chartParentWidth && newChartWrapperWidth > chartParentWidth - legendWidth ) {
                        newChartWrapperWidth = chartParentWidth - legendWidth;

                        if ( type === 'doughnut' || type === 'pie' || type === 'polarArea' || type === 'radar' ) {
                            newChartWrapperHeight = newChartWrapperWidth;
                        }
                        else {
                            newChartWrapperHeight = newChartWrapperWidth / 2;
                        }
                    }

                    setChartWrapperHeight( newChartWrapperHeight );
                    setChartWrapperWidth( newChartWrapperWidth );
                }
            }
            else {
                setChartWrapperHeight( 0 );
                setChartWrapperWidth( 0 );
            }

            if ( chartJs ) {
                chartJs.resize();
                chartJs.update();
            }
        }
    );

    // ( on update )
    // load data
    // handle color scheme changes (dark mode <-> light mode)
    // update chart dataset / data visibility
    useEffect(
        () => {
            const chartJs                              = chart.current?.getChart(),
                  newLegendItemsVisibility : boolean[] = [];

            if ( viewKey && !isLoading.current ) {
                if ( !isConfigLoaded ) {
                    void loadConfig();
                }
                else if ( !isDataLoaded ) {
                    void load();
                }
            }

            if ( chartJs && isDataLoaded ) {
                hasRendered.current = true;

                if ( elusiveData?.legendItems ) {
                    for ( let legendItemCount = 0; legendItemCount < elusiveData.legendItems.length; legendItemCount++ ) {
                        const legendItem = elusiveData.legendItems[ legendItemCount ];

                        newLegendItemsVisibility[ legendItemCount ] = legendItem.hidden;

                        if ( legendItem.hidden !== lastLegendItemsVisibility.current[ legendItemCount ] ) {
                            if ( legendItem.hidden ) {
                                onLegendItemLeave( legendItem.dataSetIndex );

                                if ( type === 'doughnut' || type === 'pie' || type === 'polarArea' ) {
                                    if ( legendItem.dataIndex !== undefined && chartJs.getDataVisibility( legendItem.dataIndex ) ) {
                                        chartJs.toggleDataVisibility( legendItem.dataIndex );
                                    }
                                }
                                else {
                                    chartJs.hide( legendItem.dataSetIndex, legendItem.dataIndex );
                                }
                            }
                            else {
                                if ( type === 'doughnut' || type === 'pie' || type === 'polarArea' ) {
                                    if ( legendItem.dataIndex !== undefined && !chartJs.getDataVisibility( legendItem.dataIndex ) ) {
                                        chartJs.toggleDataVisibility( legendItem.dataIndex );
                                    }
                                }
                                else {
                                    chartJs.show( legendItem.dataSetIndex, legendItem.dataIndex );
                                }
                            }
                        }
                    }

                    lastLegendItemsVisibility.current = newLegendItemsVisibility;
                }

                chartJs.update();
            }
            else if ( !hasRendered.current ) {
                window.setTimeout(
                    () => {
                        setRerenderToggle( rerenderToggle + 1 );
                    },
                    10
                );
            }
        }
    );

    // apply colorScheme dependent settings
    useEffect(
        () => {
            const chartJs = chart.current?.getChart();
            let processed = false;

            if ( chartJs ) {
                processed = true;

                const scaleKeys = Object.keys( chartJs.config?.options?.scales ?? {} ),
                      colors    = COLOR_SCHEME_COLORS[ colorScheme ];

                // scales
                for ( const scaleKey of scaleKeys ) {
                    chartJs.config.options.scales[ scaleKey ] = {
                        ...chartJs.config.options.scales[ scaleKey ],
                        border     : {
                            ...chartJs.config.options.scales[ scaleKey ].border,
                            color: colors.scaleBorder
                        },
                        grid       : {
                            ...chartJs.config.options.scales[ scaleKey ].grid,
                            color    : colors.scaleGrid,
                            tickColor: colors.scaleGrid
                        },
                        ticks      : {
                            ...chartJs.config.options.scales[ scaleKey ].ticks,
                            backdropColor: colors.scaleTickBackdrop,
                            color        : colors.scaleTickText,
                            major        : {
                                ...chartJs.config.options.scales[ scaleKey ].ticks?.major,
                                color: colors.scaleGrid
                            }
                        },
                        angleLines : {
                            ...chartJs.config.options.scales[ scaleKey ].angleLines,
                            color: colors.scaleGrid
                        },
                        pointLabels: {
                            ...chartJs.config.options.scales[ scaleKey ].pointLabels,
                            backdropColor: colors.scaleTickBackdrop,
                            color        : colors.scaleTickText
                        },
                        title: {
                            ...chartJs.config.options.scales[ scaleKey ].title,
                            color: colors.scaleTickText
                        }
                    };
                }

                chartJs.update();
            }

            if ( !processed ) {
                window.setTimeout(
                    () => {
                        setRerenderToggle( rerenderToggle + 1 );
                    },
                    10
                );
            }
        },
        [
            colorScheme,
            options,
            rerenderToggle,
            scales,
            type,
            JSON.stringify( chart.current?.getChart()?.config?.options?.scales )
        ]
    );

    // dataSetData
    useEffect(
        () => {
            dispatch( setElusiveData( uid, { dataSetData } ) );
        },
        [
            dataSetData
        ]
    );

    // legendItems
    useEffect(
        () => {
            dispatch( setElusiveData( uid, { legendItems } ) );
        },
        [
            legendItems
        ]
    );

    // options
    useEffect(
        () => {
            dispatch( setElusiveData( uid, { options } ) );
        },
        [
            options
        ]
    );

    // xAxisValues
    useEffect(
        () => {
            dispatch( setElusiveData( uid, { xAxisValues } ) );
        },
        [
            xAxisValues
        ]
    );

    return content;
};
Chart.defaultProps = defaultProps;
Chart.displayName = 'Chart';

export default Chart;
